import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { FC } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import {
  CheckIcon,
  ChevronDownIcon,
  CrossIcon,
  InfoCircle,
} from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { useLocale } from '../../../../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../../../../translations/stakeholder-translation.util';
import { StakeholderType } from '../../../../../../../types/stakeholder.types';
import {
  GRANTEE,
  TEMPLATE_SIGNATORY_TYPES,
  type TemplateSignatoryType,
  templateSignatoryTypeTranslation,
} from '../../../../constants/template-signatory-type.constant';
import { TemplateCategory } from '../../../../enums/template.const';
import { AddTemplateFormSchema } from '../../../../validations/add-template.validation';

const SIGNATORIES_MAX_SELECTION = 5;

const STAKEHOLDER_TYPE_OPTIONS = Object.keys(stakeholderTypeTranslation) as StakeholderType[];

export type SignatoriesComboboxProps = {
  control: Control<AddTemplateFormSchema>;
};

export const SignatoriesCombobox: FC<SignatoriesComboboxProps> = ({ control }) => {
  const { messagesLocale } = useLocale();
  const category = useWatch({ control, name: 'stepOne.category' });
  const { field, fieldState } = useController({ control, name: 'stepTwo.signatories' });
  const value = (field.value ?? []) as TemplateSignatoryType[];
  const isAtMax = value.length >= SIGNATORIES_MAX_SELECTION;
  const isGrantCategory = category === TemplateCategory.GRANT;

  const signatoryOptions: TemplateSignatoryType[] = isGrantCategory
    ? TEMPLATE_SIGNATORY_TYPES
    : STAKEHOLDER_TYPE_OPTIONS;

  const handleChange = (selected: TemplateSignatoryType[]) => {
    let next = selected;
    if (isGrantCategory && !next.includes(GRANTEE)) {
      next = [GRANTEE, ...next];
    }
    if (next.length > SIGNATORIES_MAX_SELECTION) {
      return;
    }
    field.onChange(next);
  };

  const stopChipControlPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const handleRemove = (id: TemplateSignatoryType, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isGrantCategory && id === GRANTEE) return;
    field.onChange(value.filter((v) => v !== id));
  };

  const labelFor = (v: TemplateSignatoryType) => {
    const labelKey = templateSignatoryTypeTranslation[v];
    return labelKey ? messagesLocale[labelKey] : v;
  };

  return (
    <Listbox multiple onChange={handleChange} value={value}>
      {({ open }) => (
        <div className="relative">
          <ListboxButton
            as="div"
            className={twMerge(
              'flex w-full cursor-pointer items-center justify-between gap-2 rounded-t border-b border-gray-700 bg-gray-50 px-3 py-2 text-left transition-colors',
              'focus:outline-none focus:ring-0',
              value.length > 0 ? 'min-h-[56px]' : 'h-[56px] min-h-[56px]',
              fieldState.error?.message && 'border-b-fireside-600',
              open && 'border-b-2 border-b-brand-700',
            )}
          >
            {value.length > 0 ? (
              <>
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
                  {value.map((v) => (
                    <span
                      className="inline-flex items-center gap-1 rounded-[32px] bg-brand-50 px-2 py-0.5 text-sm font-normal text-gray-700"
                      key={v}
                    >
                      {labelFor(v)}
                      {!(isGrantCategory && v === GRANTEE) && (
                        <button
                          className="rounded px-0.5"
                          onClick={(e) => handleRemove(v, e)}
                          onMouseDown={stopChipControlPropagation}
                          onMouseDownCapture={stopChipControlPropagation}
                          onPointerDown={stopChipControlPropagation}
                          onPointerDownCapture={stopChipControlPropagation}
                          type="button"
                        >
                          <CrossIcon className="h-3.5 w-3.5" iconColor="#344054" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                <ChevronDownIcon
                  className={twMerge(
                    'h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                />
              </>
            ) : (
              <>
                <span className="text-sm text-gray-400">
                  <AppFormattedMessage id="CHOOSE" />
                </span>
                <ChevronDownIcon
                  className={twMerge(
                    'h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                />
              </>
            )}
          </ListboxButton>
          {fieldState.error?.message && (
            <div className="mt-3 flex gap-1">
              <InfoCircle className="size-[14px] pt-0.5" />
              <div className="whitespace-pre-line text-left font-inter text-xs font-[450] text-fireside-600">
                {fieldState.error.message}
              </div>
            </div>
          )}
          <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white p-2 shadow-xl">
            {isAtMax && (
              <div className="mb-2 flex items-center gap-1.5 px-2 py-1 text-xs text-fireside-600">
                <InfoCircle className="h-4 w-4 shrink-0" iconColor="#D92D20" />
                <AppFormattedMessage id="MAX_5_SIGNATORIES" />
              </div>
            )}
            {signatoryOptions.map((optValue) => {
              const selected = value.includes(optValue);
              const isGranteeLocked = isGrantCategory && optValue === GRANTEE && selected;
              const blockedByMax = !selected && isAtMax;

              return (
                <ListboxOption
                  aria-disabled={isGranteeLocked ? true : undefined}
                  className={(props) =>
                    twMerge(
                      'rounded px-2 py-1.5 text-sm',
                      blockedByMax && 'cursor-not-allowed text-gray-300',
                      isGranteeLocked &&
                        'pointer-events-none cursor-not-allowed bg-gray-50 text-gray-700',
                      !blockedByMax &&
                        !isGranteeLocked &&
                        twMerge('cursor-pointer text-gray-700', props.selected && 'bg-gray-50'),
                    )
                  }
                  disabled={blockedByMax}
                  key={optValue}
                  value={optValue}
                >
                  {({ selected: isSelected }) => {
                    const lockedSelected = isGranteeLocked && isSelected;

                    return (
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex h-6 items-center gap-2">
                          <span
                            aria-hidden
                            className={twMerge(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded-md border',
                              lockedSelected && 'border-gray-200 bg-gray-200',
                              isSelected && !lockedSelected && 'border-brand-500 bg-brand-500',
                              !isSelected && 'border-gray-300 bg-white',
                              blockedByMax && !isSelected && 'border-gray-200 bg-gray-50',
                            )}
                          >
                            {isSelected && (
                              <CheckIcon
                                className="h-2.5 w-2.5"
                                iconColor={lockedSelected ? '#D0D5DD' : '#fff'}
                              />
                            )}
                          </span>
                          <AppFormattedMessage id={templateSignatoryTypeTranslation[optValue]} />
                        </span>
                      </div>
                    );
                  }}
                </ListboxOption>
              );
            })}
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};
